// src/utils/indexedDB.js

const DB_NAME = 'AnomalyDetectionDB';
const STORE_NAME = 'files';
const DB_VERSION = 1;

let db;

/**
 * Initializes the IndexedDB database.
 * @returns {Promise<IDBDatabase>} A promise that resolves with the database instance.
 */
export const initDB = () => {
    return new Promise((resolve, reject) => {
        if (db) {
            return resolve(db);
        }

        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onerror = (event) => {
            console.error('IndexedDB error:', event.target.error);
            // Translated
            reject('Fehler beim Öffnen der Datenbank.');
        };

        request.onsuccess = (event) => {
            db = event.target.result;
            resolve(db);
        };

        request.onupgradeneeded = (event) => {
            const dbInstance = event.target.result;
            if (!dbInstance.objectStoreNames.contains(STORE_NAME)) {
                dbInstance.createObjectStore(STORE_NAME, { keyPath: 'name' });
            }
        };
    });
};

/**
 * --- UPDATED ---
 * Saves one or more File objects to IndexedDB. Overwrites existing files with the same name.
 * @param {File[]} fileObjects An array of files to save.
 * @returns {Promise<void>} A promise that resolves when all files are saved.
 */
export const saveFiles = async (fileObjects) => {
    const db = await initDB();
    return new Promise((resolve, reject) => {
        if (!fileObjects || fileObjects.length === 0) {
            return resolve();
        }
        
        const transaction = db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        let error = null;

        transaction.onerror = (event) => {
            // Translated
            reject(error || 'Transaktion fehlgeschlagen: ' + event.target.error);
        };
        transaction.oncomplete = () => {
            resolve();
        };

        fileObjects.forEach(file => {
            const request = store.put(file);
            request.onerror = (e) => {
                error = e.target.error;
                console.error('Error saving file:', file.name, e.target.error);
                transaction.abort();
            };
        });
    });
};


/**
 * Retrieves all files from IndexedDB.
 * @returns {Promise<File[]>} A promise that resolves with an array of file objects.
 */
export const getAllFiles = async () => {
    const db = await initDB();
    return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.getAll();

        request.onsuccess = (event) => resolve(event.target.result);
        request.onerror = (event) => {
            console.error('Error fetching files:', event.target.error);
            // Translated
            reject('Dateien konnten nicht aus der Datenbank abgerufen werden.');
        };
    });
};

/**
 * --- NEW FUNCTION ---
 * Deletes a single file from IndexedDB by its name (key).
 * @param {string} fileName The key of the file to delete.
 * @returns {Promise<void>} A promise that resolves when the file is deleted.
 */
export const deleteFile = async (fileName) => {
    const db = await initDB();
    return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.delete(fileName);

        request.onsuccess = () => resolve();
        request.onerror = (event) => {
            console.error('Error deleting file:', event.target.error);
            // Translated
            reject('Datei konnte nicht aus der Datenbank gelöscht werden.');
        };
    });
};


/**
 * Clears all files from the object store.
 * @returns {Promise<void>} A promise that resolves when the store is cleared.
 */
export const clearAllFiles = async () => {
    const db = await initDB();
    return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.clear();

        request.onsuccess = () => resolve();
        request.onerror = (event) => {
            console.error('Error clearing file store:', event.target.error);
            // Translated
            reject('Die Dateidatenbank konnte nicht geleert werden.');
        };
    });
};